import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import { sensorUnits } from 'commons/src/constants';
import { EnumStatusColors, SensorTypes } from 'commons/src/models/commonEnums';
import { notUnitTopSensors } from '../../constants';
import { DeviceSensorDetailsType } from '../../models/commons';
import { Store } from '../../reducers';

type SensorRange = {
    [key: string]: string;
};

type Summary = {
    text: string;
    params: Record<string, string>;
};

export const getSummary = (
    sensorType: SensorTypes,
    isExpanded: boolean,
    sensorDetail?: DeviceSensorDetailsType
): Summary => {
    switch (sensorType) {
        case 'humidity':
            return {
                text: isExpanded ? 'PublicIAQDashboard.HumidityDescription' : 'PublicIAQDashboard.HumiditySummary',
                params: {},
            };
        case 'voc':
            return {
                text: isExpanded ? 'PublicIAQDashboard.TVOCDescription' : 'PublicIAQDashboard.TVOCSummary',
                params: {},
            };
        case 'co2':
            return {
                text: isExpanded ? 'PublicIAQDashboard.CO2Description' : 'PublicIAQDashboard.CO2Summary',
                params: {},
            };
        case 'pm25':
            return {
                text: isExpanded ? 'PublicIAQDashboard.PM25Description' : 'PublicIAQDashboard.PM25Summary',
                params: {},
            };
        case 'temp': {
            const goodTempDetails = sensorDetail && sensorDetail.ranges?.find(sensor => sensor.rating === 'GOOD');
            const goodTempRange: SensorRange = {
                lowThreshold:
                    goodTempDetails && goodTempDetails.from !== undefined ? goodTempDetails.from.toString() : '',
                highThreshold: goodTempDetails && goodTempDetails.to !== undefined ? goodTempDetails.to.toString() : '',
                tempUnit: (sensorDetail?.unit && (sensorDetail.unit as string).toUpperCase()) || '',
            };
            return {
                text: isExpanded ? 'PublicIAQDashboard.TempDescription' : 'PublicIAQDashboard.TempSummary',
                params: goodTempRange,
            };
        }
        default:
            return { text: '', params: {} };
    }
};

export const getTitle = (sensorType: SensorTypes): string => {
    switch (sensorType) {
        case 'humidity':
            return 'humidity';
        case 'voc':
            return 'PublicIAQDashboard.TVOCTitle';
        case 'co2':
            return 'PublicIAQDashboard.CO2Title';
        case 'temp':
            return 'Temperature';
        default:
            return sensorType;
    }
};

export const getDotColor = (sensorValue?: number, sensorDetails?: DeviceSensorDetailsType) => {
    if (sensorValue === undefined) {
        return EnumStatusColors.grey;
    }
    const sensorRange =
        sensorDetails &&
        sensorDetails.ranges?.find(range => {
            const isAboveFrom = range.from ? range.from <= sensorValue : true;
            const isBelowTo = range.to ? range.to > sensorValue : true;
            return isAboveFrom && isBelowTo;
        });
    const rating = sensorRange && sensorRange.rating;

    switch (rating) {
        case 'POOR':
            return EnumStatusColors.red;
        case 'FAIR':
            return EnumStatusColors.yellow;
        default:
            return EnumStatusColors.green;
    }
};

interface ParentProps {
    sensorType: SensorTypes;
    sensorValue: number;
}

interface StateProps {
    sensorDetails?: { [sensor: string]: DeviceSensorDetailsType };
}

export type Props = ParentProps & StateProps;

export const PublicSensorTile = (props: Props): React.ReactElement => {
    const { sensorType, sensorDetails, sensorValue } = props;

    const { t: txt } = useTranslation();
    const sensorDetail = sensorDetails && sensorDetails[sensorType];
    const dotColor = getDotColor(sensorValue, sensorDetail);
    const unit = sensorDetail && sensorUnits[sensorDetail.unit];
    const unitTop = !notUnitTopSensors.includes(sensorType);

    const [isExpanded, setExpanded] = useState(false);

    const tileTitle = (
        <div className={classNames('tile-title', { expanded: isExpanded })}>{txt(getTitle(sensorType))}</div>
    );

    const sensorValueInfo = (
        <div className={classNames('info__section__left', { expanded: isExpanded })}>
            <div className={classNames('icons', { expanded: isExpanded })}>
                <div className="info__sensor-icon">
                    <SensorIcon sensor={sensorType} />
                </div>
                <div // TODO - replace this or does it have its own styling?
                    className={classNames('sensor__line', {
                        'sensor__line--ok': dotColor === EnumStatusColors.green,
                        'sensor__line--alert': dotColor === EnumStatusColors.red,
                        'sensor__line--warning': dotColor === EnumStatusColors.yellow,
                    })}
                />
            </div>
            <div className="readings">
                {unitTop && <div className="info__unit--top">{unit}</div>}

                <span className="info__sensor-reading">
                    {sensorValue}
                    {!unitTop && <span className={classNames({ info__unit: unit === sensorUnits.pct })}>{unit}</span>}
                </span>
                <div className="info__short-title">{txt(sensorType)}</div>
            </div>
        </div>
    );

    const arrow = (
        <div className={classNames('arrow', { expanded: isExpanded })}>
            <div className={classNames('arrow__icon', { expanded: isExpanded })} />
        </div>
    );

    const tileSummary = getSummary(sensorType, isExpanded, sensorDetail);

    return (
        <div
            className={classNames('public-tile-wrapper', { expanded: isExpanded })}
            role="button"
            onClick={() => setExpanded(!isExpanded)}
            onKeyUp={e => e.key === 'Enter' && setExpanded(!isExpanded)}
            aria-label={txt('PublicIAQDashboard.ExpandTile')}
            tabIndex={0}
            data-testid="expand-collapse-tile"
        >
            {!isExpanded ? (
                <div className="public-tile-body">
                    {tileTitle}
                    <div className="info__section">
                        {sensorValueInfo}
                        <div className="info__summary">{txt(tileSummary.text, tileSummary.params)}</div>
                    </div>
                    {arrow}
                </div>
            ) : (
                <div className="public-tile-body expanded">
                    {sensorValueInfo}
                    {tileTitle}
                    <div className="info__expanded-summary">{txt(tileSummary.text, tileSummary.params)}</div>
                    {arrow}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        publicDeviceData: { sensorDetails },
    } = state;
    return {
        sensorDetails,
    };
};

export default connect(mapStateToProps)(PublicSensorTile);
